import type { components } from '@connected/api-client/api.generated';

import { OBSERVATION_COLOR_CODES } from './observation-constants';

export type ObservationQueryResultReport = components['schemas']['ListElectricalProjectObservationQueryResultReport'];
export type ElectricalInspectionKind = components['schemas']['ElectricalInspectionKind'];
export type ElectricalObservationClassificationCodeKind = components['schemas']['ElectricalObservationClassificationCodeKind'];

/**
 * Represents a scope with its severity information for display purposes.
 */
export type ScopeSeverity = {
    inspectionKind: ElectricalInspectionKind;
    codeKind: ElectricalObservationClassificationCodeKind;
    hexColor: string;
};

/**
 * Severity priority order from highest to lowest.
 * Used for sorting and determining the "main" severity.
 */
export const SEVERITY_PRIORITY_ORDER: ElectricalObservationClassificationCodeKind[] = [
    'Severe',
    'Serious',
    'Minor',
    'Remark',
];

/**
 * Extracts active scope severities from observation reports.
 * Only returns reports that are enabled and have a valid code kind.
 */
export function getActiveScopeSeverities(
    reports: ObservationQueryResultReport[] | null | undefined,
): ScopeSeverity[] {
    if (!reports || reports.length === 0) {
        return [];
    }

    return reports
        ?.filter((report): report is ObservationQueryResultReport & {
            InspectionKind: ElectricalInspectionKind;
            CodeKind: ElectricalObservationClassificationCodeKind;
        } => {
            return (
                report.Enabled === true
                && report.InspectionKind != null
                && report.CodeKind != null
            );
        })
        .map(report => ({
            inspectionKind: report.InspectionKind,
            codeKind: report.CodeKind,
            hexColor: report.Code?.HexColorCode ?? OBSERVATION_COLOR_CODES[report.CodeKind] ?? '#000000',
        }));
}

/**
 * Sorts scope severities by priority (highest severity first).
 */
export function sortBySeverityPriority(severities: ScopeSeverity[]): ScopeSeverity[] {
    return [...severities].sort((a, b) => {
        const aIndex = SEVERITY_PRIORITY_ORDER.indexOf(a.codeKind);
        const bIndex = SEVERITY_PRIORITY_ORDER.indexOf(b.codeKind);
        return aIndex - bIndex;
    });
}

/**
 * Gets the highest severity from a list of scope severities.
 * Returns null if the list is empty.
 */
export function getHighestSeverity(
    severities: ScopeSeverity[],
): ScopeSeverity | null {
    if (severities.length === 0) {
        return null;
    }
    return sortBySeverityPriority(severities)[0];
}

/**
 * Gets the color for a given code kind.
 * Falls back to black if not found.
 */
export function getSeverityColor(
    codeKind: ElectricalObservationClassificationCodeKind | null | undefined,
): string {
    if (!codeKind) {
        return '#000000';
    }
    return OBSERVATION_COLOR_CODES[codeKind] ?? '#000000';
}
